"use client";

import type { HTMLAttributes } from "react";
import type { RecipeVariants } from "@vanilla-extract/recipes";
import { mergeClasses } from "@huub-web/utils";

import { flexStyle } from "./Flex.css";
import {
  type ChildrenProp,
  type ClassNameProp,
  polymorphicForwardRef,
} from "../../props";

export type FlexProps = HTMLAttributes<HTMLElement> & {
  label?: string;
} & ChildrenProp &
  ClassNameProp &
  RecipeVariants<typeof flexStyle> & {
    grow?: boolean;
    shrink?: boolean;
    basis?: string;
    hideWhenEmpty?: boolean;
  };

/**
 * Flex is a component that allows you to quickly create a flexbox layout.
 */
export const Flex = polymorphicForwardRef<"div", FlexProps>(
  (
    {
      as: Element = "div",
      direction,
      justify,
      basis,
      align,
      gap,
      wrap = false,
      inline = false,
      grow,
      padding,
      shrink,
      width,
      hideWhenEmpty = false,
      overflowHidden = false,
      children,
      className,
      ...props
    },
    ref,
  ) => (
    <Element
      ref={ref}
      className={mergeClasses(
        flexStyle({
          wrap,
          direction,
          justify,
          align,
          inline,
          gap,
          grow,
          padding,
          shrink,
          width,
          hideWhenEmpty,
          overflowHidden,
        }),
        className,
      )}
      style={{ flexBasis: basis }}
      {...props}
    >
      {children}
    </Element>
  ),
);

Flex.displayName = "Flex";
